import { useMachine } from "@xstate/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  type AuthProvisioningStatus,
  useGetMyProvisioningStatus,
} from "@elation/api-client/src/stitch-web";
import { PracticeTimezone } from "@elation/api-client/src/stitch-web/models/practiceTimezone";

import { useCurrentTime } from "@src/shared/hooks/useCurrentTime";
import { navigationV2 } from "@src/shared/navigation/v2";
import {
  is401Error,
  is404Error,
  isNetworkError,
} from "@src/shared/utils/http/errors";
import {
  captureException,
  captureMessage,
} from "@src/shared/utils/observability/sentry";

import { authClient } from "../../data";
import { type SignUpStepId } from "../../domain";
import {
  buildProvisioningSupportHref,
  getPendingMessage,
  getProvisioningFailureView,
  getProvisioningStatusRefetchInterval,
  PROVISIONING_PENDING_TIMEOUT_MS,
  signUpProvisioningMachine,
} from "../../domain/v2";
import type {
  SignUpAccountValues,
  SignUpProvisioningView,
} from "../auth-flow.types";
import { waitForSession } from "../waitForSession";

const FALLBACK_TIMEZONE = PracticeTimezone["America/Chicago"];
const PRACTICE_TIMEZONES = new Set<string>(Object.values(PracticeTimezone));
const STEP_PARAM = "step";
const STEP_BY_NUMBER: Record<number, SignUpStepId> = {
  1: "profile",
  2: "verify-email",
  3: "microphone",
  4: "preview",
  5: "trust",
};
const STEP_NUMBER_BY_ID: Record<SignUpStepId, number> = {
  profile: 1,
  "verify-email": 2,
  microphone: 3,
  preview: 4,
  trust: 5,
};

function getDefaultTimezone() {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return PRACTICE_TIMEZONES.has(timezone) ? timezone : FALLBACK_TIMEZONE;
}

function getEmailVerificationCallbackURL() {
  return `${window.location.origin}${navigationV2.signUp()}?${STEP_PARAM}=2`;
}

function getUnexpectedProvisioningErrorReference(
  error: unknown,
): string | undefined {
  if (!error || typeof error !== "object") return undefined;

  const response = (
    error as { response?: { data?: unknown; headers?: unknown } }
  ).response;
  const data = response?.data;

  if (data && typeof data === "object") {
    for (const key of ["requestId", "traceId", "errorId"] as const) {
      const value = (data as Record<string, unknown>)[key];
      if (typeof value === "string" && value.length > 0) return value;
    }
  }

  const headers = response?.headers as Record<string, unknown> | undefined;
  const headerValue = headers?.["x-request-id"] ?? headers?.["x-trace-id"];

  return typeof headerValue === "string" && headerValue.length > 0
    ? headerValue
    : undefined;
}

export function useAccountCreationFlow() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [emailVerificationError, setEmailVerificationError] = useState<
    string | null
  >(null);
  const [isEmailVerificationChecking, setIsEmailVerificationChecking] =
    useState(false);
  const [isVerificationEmailResending, setIsVerificationEmailResending] =
    useState(false);
  const [verificationEmail, setVerificationEmail] = useState<string | null>(
    null,
  );
  const activeStep =
    STEP_BY_NUMBER[Number(searchParams.get(STEP_PARAM))] ?? "profile";
  const nowMs = useCurrentTime();

  useEffect(() => {
    const current = searchParams.get(STEP_PARAM);
    if (STEP_BY_NUMBER[Number(current)]) return;

    const next = new URLSearchParams(searchParams);
    next.set(STEP_PARAM, "1");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const setActiveStep = useCallback(
    (step: SignUpStepId) => {
      const next = new URLSearchParams(searchParams);
      next.set(STEP_PARAM, String(STEP_NUMBER_BY_ID[step]));
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const [snapshot, send] = useMachine(signUpProvisioningMachine, {
    input: {
      createAccount: async (values) => {
        const provisioningFields = {
          firstName: values.firstName,
          lastName: values.lastName,
          specialty: "",
          timezone: getDefaultTimezone(),
          type: "physician" as const,
        };
        const result = await authClient.signUp.email({
          ...provisioningFields,
          callbackURL: getEmailVerificationCallbackURL(),
          email: values.email,
          name: `${values.firstName} ${values.lastName}`,
          password: values.password,
        });

        if (result.error) {
          throw new Error(result.error.message ?? "Unable to create account.");
        }

        if (!result.data?.user) {
          throw new Error("Unable to create account.");
        }

        setVerificationEmail(result.data.user.email);
        const needsEmailVerification = !result.data.user.emailVerified;

        await waitForSession({
          attempts: 3,
          delayMs: 150,
          reportOnExhaustion: false,
        });

        return {
          email: values.email,
          needsEmailVerification,
        };
      },
    },
  });
  const provisioningPollingStartedAtRef = useRef<number | null>(null);
  const wasCreatingAccountRef = useRef(false);

  const pendingStartedAt = snapshot.context.pendingStartedAt;
  const pendingElapsedMs = pendingStartedAt ? nowMs - pendingStartedAt : 0;
  const shouldResumeProvisioning =
    activeStep !== "profile" &&
    activeStep !== "verify-email" &&
    snapshot.matches("idle");
  const shouldResumeEmailVerification =
    activeStep === "verify-email" && snapshot.matches("idle");
  const isProvisioningPending = snapshot.matches({ provisioning: "pending" });
  const isEmailVerificationPending = snapshot.matches({
    emailVerification: "pending",
  });
  const shouldPollProvisioning =
    isProvisioningPending || shouldResumeProvisioning;
  const isCreatingAccount = snapshot.matches("creatingAccount");

  useEffect(() => {
    const shouldAutoAdvance =
      activeStep === "profile" &&
      (isProvisioningPending || isEmailVerificationPending) &&
      wasCreatingAccountRef.current;

    wasCreatingAccountRef.current = isCreatingAccount;

    if (!shouldAutoAdvance) return;

    setActiveStep(isEmailVerificationPending ? "verify-email" : "microphone");
  }, [
    activeStep,
    isCreatingAccount,
    isEmailVerificationPending,
    isProvisioningPending,
    setActiveStep,
  ]);

  useEffect(() => {
    if (!shouldResumeProvisioning) return;

    send({ type: "RESUME_PROVISIONING" });
  }, [send, shouldResumeProvisioning]);

  const readVerificationSession = useCallback(async () => {
    const { data } = await authClient.getSession({
      query: { disableCookieCache: true },
    });

    return data;
  }, []);

  const checkEmailVerification = useCallback(async (): Promise<boolean> => {
    setIsEmailVerificationChecking(true);
    setEmailVerificationError(null);

    try {
      const data = await readVerificationSession();

      if (data?.session) {
        setVerificationEmail(data.user.email);
      }

      if (data?.session && data.user.emailVerified) {
        send({ type: "EMAIL_VERIFIED" });
        return true;
      }

      setEmailVerificationError(
        "We have not detected a verified session yet. Use the link in your email, then try again.",
      );
      return false;
    } catch (error) {
      captureException(error, {
        tags: {
          route: "v2/sign-up",
          operation: "checkEmailVerification",
        },
      });
      setEmailVerificationError(
        "We could not check your verification status. Please try again.",
      );
      return false;
    } finally {
      setIsEmailVerificationChecking(false);
    }
  }, [readVerificationSession, send]);

  const pollEmailVerification = useCallback(async (): Promise<void> => {
    try {
      const data = await readVerificationSession();

      if (data?.session) {
        setVerificationEmail(data.user.email);
      }

      if (data?.session && data.user.emailVerified) {
        send({ type: "EMAIL_VERIFIED" });
      }
    } catch {
      return;
    }
  }, [readVerificationSession, send]);

  useEffect(() => {
    if (!isEmailVerificationPending && !shouldResumeEmailVerification) return;

    const intervalId = window.setInterval(() => {
      void pollEmailVerification();
    }, 2_500);

    void pollEmailVerification();

    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    isEmailVerificationPending,
    pollEmailVerification,
    shouldResumeEmailVerification,
  ]);

  useEffect(() => {
    if (activeStep === "verify-email" && isProvisioningPending) {
      setActiveStep("microphone");
    }
  }, [activeStep, isProvisioningPending, setActiveStep]);

  const resendVerificationEmail = async () => {
    if (isVerificationEmailResending) return;

    if (!verificationEmail) {
      setEmailVerificationError(
        "We could not find the email address for this signup. Please go back and try again.",
      );
      return;
    }

    setIsVerificationEmailResending(true);
    setEmailVerificationError(null);

    try {
      const result = await authClient.sendVerificationEmail({
        email: verificationEmail,
        callbackURL: getEmailVerificationCallbackURL(),
      });

      if (result.error) {
        throw new Error(
          result.error.message ?? "Unable to resend verification email.",
        );
      }
    } catch (error) {
      captureException(error, {
        tags: {
          route: "v2/sign-up",
          operation: "sendVerificationEmail",
        },
      });
      setEmailVerificationError(
        error instanceof Error
          ? error.message
          : "Unable to resend verification email.",
      );
    } finally {
      setIsVerificationEmailResending(false);
    }
  };

  useEffect(() => {
    if (shouldPollProvisioning) {
      provisioningPollingStartedAtRef.current ??= Date.now();
      return;
    }

    provisioningPollingStartedAtRef.current = null;
  }, [shouldPollProvisioning]);

  const provisioning = useGetMyProvisioningStatus({
    query: {
      enabled: shouldPollProvisioning,
      refetchOnMount: "always",
      retry: false,
      staleTime: 0,
      refetchInterval: (query) => {
        if (!shouldPollProvisioning) return false;

        return getProvisioningStatusRefetchInterval({
          data: query.state.data as { data?: AuthProvisioningStatus },
          fetchFailureCount: query.state.fetchFailureCount,
          hasError: Boolean(query.state.error),
          isNetworkError: isNetworkError(query.state.error),
          isNotFoundError: is404Error(query.state.error),
          pendingElapsedMs: pendingStartedAt
            ? Date.now() - pendingStartedAt
            : 0,
        });
      },
    },
  });

  const status = provisioning.data?.data;

  useEffect(() => {
    if (!shouldPollProvisioning || !status) return;

    const pollingStartedAt = provisioningPollingStartedAtRef.current;
    if (!pollingStartedAt || provisioning.dataUpdatedAt < pollingStartedAt) {
      return;
    }

    send({ type: "PROVISIONING_STATUS", status });
  }, [provisioning.dataUpdatedAt, send, shouldPollProvisioning, status]);

  useEffect(() => {
    if (
      !shouldPollProvisioning ||
      pendingElapsedMs < PROVISIONING_PENDING_TIMEOUT_MS
    ) {
      return;
    }

    send({ type: "PROVISIONING_TIMED_OUT" });
  }, [pendingElapsedMs, send, shouldPollProvisioning]);

  useEffect(() => {
    if (!shouldPollProvisioning || !provisioning.error) return;

    const pollingStartedAt = provisioningPollingStartedAtRef.current;
    if (!pollingStartedAt || provisioning.errorUpdatedAt < pollingStartedAt) {
      return;
    }

    if (is401Error(provisioning.error)) {
      send({ type: "PROVISIONING_UNAUTHORIZED" });
      return;
    }

    if (is404Error(provisioning.error)) {
      send({ type: "PROVISIONING_NOT_FOUND" });
      return;
    }

    if (isNetworkError(provisioning.error)) return;

    send({
      type: "PROVISIONING_UNEXPECTED_ERROR",
      requestId: getUnexpectedProvisioningErrorReference(provisioning.error),
    });
  }, [
    provisioning.error,
    provisioning.errorUpdatedAt,
    send,
    shouldPollProvisioning,
  ]);

  useEffect(() => {
    if (status?.state !== "failed") return;

    console.error("[v2/sign-up] account provisioning failed", {
      requestId: status.requestId,
      failureReason: status.failureReason,
    });
    captureMessage("[v2/sign-up] account provisioning failed", {
      level: "error",
      extra: {
        requestId: status.requestId,
        failureReason: status.failureReason,
      },
    });
  }, [status]);

  useEffect(() => {
    const error = provisioning.error;

    if (
      !error ||
      isNetworkError(error) ||
      is404Error(error) ||
      is401Error(error)
    ) {
      return;
    }

    console.error("[v2/sign-up] unexpected provisioning status error", error);
    captureException(error, {
      tags: {
        route: "v2/sign-up",
        operation: "getMyProvisioningStatus",
      },
    });
  }, [provisioning.error]);

  useEffect(() => {
    if (snapshot.matches({ provisioning: "unauthorized" })) {
      navigate(navigationV2.login(), { replace: true });
    }
  }, [navigate, snapshot]);

  const createAccount = (values: SignUpAccountValues) => {
    if (
      snapshot.matches("creatingAccount") ||
      snapshot.matches("emailVerification") ||
      snapshot.matches("provisioning")
    ) {
      return;
    }

    send({ type: "SUBMIT_ACCOUNT", values });
  };

  const complete = () => {
    if (snapshot.matches({ provisioning: "succeeded" })) {
      navigate(navigationV2.home(), { replace: true });
    }
  };

  const skip = () => {
    if (snapshot.matches({ provisioning: "succeeded" })) {
      navigate(navigationV2.home(), { replace: true });
      return;
    }

    if (snapshot.matches("provisioning") || shouldResumeProvisioning) {
      setActiveStep("trust");
    }
  };

  const provisioningView = useMemo<SignUpProvisioningView>(() => {
    if (shouldResumeProvisioning) {
      return {
        state: "pending",
        message: getPendingMessage(0),
      };
    }

    if (!snapshot.matches("provisioning")) {
      return { state: "idle" };
    }

    if (snapshot.matches({ provisioning: "succeeded" })) {
      return {
        state: "succeeded",
        message: "Account setup complete.",
      };
    }

    if (snapshot.matches({ provisioning: "failed" })) {
      const failureStatus = snapshot.context.provisioningStatus;

      if (failureStatus?.state === "failed") {
        const failureView = getProvisioningFailureView(failureStatus);

        return {
          state: "failed",
          ...failureView,
          signInHref: failureView.signInRecommended
            ? navigationV2.login()
            : undefined,
        };
      }

      const requestId = snapshot.context.unexpectedErrorReference ?? undefined;

      return {
        state: "failed",
        title: "Account setup failed",
        description: requestId
          ? "Something went wrong while setting up your account. Please contact support and reference the ID below."
          : "Something went wrong while setting up your account. Please contact support for help.",
        requestId,
        supportHref: buildProvisioningSupportHref(requestId),
      };
    }

    if (snapshot.matches({ provisioning: "timedOut" })) {
      const requestId = snapshot.context.provisioningStatus?.requestId;

      return {
        state: "failed",
        title: "Account setup is taking unusually long",
        description: requestId
          ? "Please contact support and reference the ID below."
          : "Please contact support for help finishing account setup.",
        requestId,
        supportHref: buildProvisioningSupportHref(requestId),
      };
    }

    return {
      state: "pending",
      message: getPendingMessage(pendingElapsedMs),
    };
  }, [pendingElapsedMs, shouldResumeProvisioning, snapshot]);

  return {
    accountError: snapshot.context.accountError,
    activeStep,
    complete,
    createAccount,
    emailVerificationError,
    enabled: true,
    isAccountSubmitting: isCreatingAccount,
    isEmailVerificationChecking,
    isEmailVerificationPending:
      isEmailVerificationPending || shouldResumeEmailVerification,
    isVerificationEmailResending,
    checkEmailVerification,
    provisioningView,
    resendVerificationEmail,
    setActiveStep,
    skip,
    verificationEmail: verificationEmail ?? undefined,
  } as const;
}
